import { useRef, useMemo, useState, useCallback } from 'react';
import { useDateRangePickerState } from '@react-stately/datepicker';
import { tv } from '@simplywallst/ui-core/next';
import { useDateRangePicker } from '@react-aria/datepicker';
import type { AriaDateRangePickerProps } from '@react-aria/datepicker';
import { DateInput } from '@/features/portfolio/components/DateInput';
import { styles } from './styles';
import { parseDate } from '@internationalized/date';

const textField = tv({
  extend: styles,
});

export type ValidationState = {
  isValid: boolean;
};

export const DateRangePicker = ({
  className,
  lang,
  'aria-label': ariaLabel,
  isDisabled,
  minValue,
  maxValue,
  defaultValue,
  onChange,
  onValidationChange,
  ...props
}: Omit<
  AriaDateRangePickerProps<ReturnType<typeof parseDate>>,
  'minValue' | 'maxValue' | 'onChange' | 'defaultValue'
> & {
  lang: 'en-GB' | 'en-US' | 'en-CA';
  className?: string;
  isDisabled?: boolean;
  minValue: Date;
  maxValue: Date;
  onChange: (value: { start: Date; end: Date }) => void;
  defaultValue: { start: Date; end: Date };
  onValidationChange?: (validationState: ValidationState) => void;
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const parsedMinValue = useMemo(
    () => parseDate(minValue.toISOString().split('T')[0]),
    [minValue]
  );
  const parsedMaxValue = useMemo(
    () => parseDate(maxValue.toISOString().split('T')[0]),
    [maxValue]
  );

  const parsedDefaultValue = useMemo(() => {
    return {
      start: parseDate(defaultValue.start.toISOString().split('T')[0]),
      end: parseDate(defaultValue.end.toISOString().split('T')[0]),
    };
  }, [defaultValue]);

  const state = useDateRangePickerState({
    ...props,
    minValue: parsedMinValue,
    maxValue: parsedMaxValue,
    defaultValue: parsedDefaultValue,
    onChange: (value) => {
      // value: RangeValue<CalendarDate>
      // Despite the type being RangeValue<CalendarDate>, it can be null
      if (value === null) return;
      const parsedValue = {
        start: new Date(value.start.toString()),
        end: new Date(value.end.toString()),
      };
      onChange(parsedValue);
    },
  });

  const ref = useRef(null);
  const { groupProps, startFieldProps, endFieldProps } = useDateRangePicker(
    props,
    state,
    ref
  );

  const styles = textField({
    isDisabled,
  });

  const dateRangeError = useMemo(() => {
    const range = state.value;
    if (!range || !parsedMinValue || !parsedMaxValue) {
      return null;
    }

    const { start, end } = range;

    if (!start || !end) return null;

    // Check minimum date constraint
    if (start < parsedMinValue) {
      return `Start date can't be before ${parsedMinValue.year}`;
    }

    // Check maximum date constraint
    if (end > parsedMaxValue) {
      return "End date can't be in the future";
    }

    // Check range validity
    if (start > end) {
      return "Start date can't be after end date";
    }

    return null;
  }, [parsedMaxValue, parsedMinValue, state.value]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);

    onValidationChange?.({ isValid: true });
  }, [onValidationChange]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);

    onValidationChange?.({
      isValid: !dateRangeError,
    });
  }, [dateRangeError, onValidationChange]);

  return (
    <div aria-label={ariaLabel} className={className}>
      <div {...groupProps} ref={ref}>
        <fieldset className="flex flex-col gap-x1">
          <div className="flex flex-1 justify-between">
            <div className={styles.input()}>
              <DateInput
                className={styles.inputEl()}
                {...startFieldProps}
                lang={lang}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
            <span className="content-center px-x0_5 text-soft md:px-x2">
              &#x2192;
            </span>
            <div className={styles.input()}>
              <DateInput
                className={styles.inputEl()}
                {...endFieldProps}
                lang={lang}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            </div>
          </div>
          {dateRangeError && !isFocused && (
            <span className={styles.error()}>{dateRangeError}</span>
          )}
        </fieldset>
      </div>
    </div>
  );
};
